import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { fetchPublicBusiness } from '../utils/publicBusiness';
import { bookPublicAppointment, publicBookingError } from '../utils/publicBooking';
import { localDateStr } from '../utils/customers';
import { MapPin, Calendar, Clock, ChevronLeft, CheckCircle, Search, Package, Star, QrCode, Trash2, ShieldCheck, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import PublicOrderPortal from './PublicOrderPortal';

export default function BookingPortal({ slug: propSlug }) {
  const { slug: paramSlug } = useParams();
  const slug = propSlug || paramSlug;

  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados del portal
  const [activePortalTab, setActivePortalTab] = useState('booking'); // booking, staff, verificar
  const [themeColor, setThemeColor] = useState('#10b981'); 
  const [showStaffTab, setShowStaffTab] = useState(true);

  // Estados de la reserva (Flujo Premium)
  const [step, setStep] = useState(1); // 1: Servicios, 2: Profesional, 3: Fecha/Hora, 4: Detalles, 5: Éxito
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos los servicios');
  
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [timeFilter, setTimeFilter] = useState('tarde'); // mañana, tarde, noche
  
  const [clientData, setClientData] = useState({ name: '', phone: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookedCode, setBookedCode] = useState('');
  
  // Modal VIP
  const [showVipModal, setShowVipModal] = useState(false);

  // Verificación
  const [verificationSearchCode, setVerificationSearchCode] = useState('');
  const [verifiedAppointment, setVerifiedAppointment] = useState(null);
  const [searchError, setSearchError] = useState('');

  const isQuoteService = (s) => !s || s.precio === null || s.precio === undefined || s.precio_cotizar || (Number(s.precio) === 0 && s.precio_cotizar);

  // Generar fechas (Próximos 14 días)
  const [availableDates, setAvailableDates] = useState([]);
  useEffect(() => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      dates.push(d);
    }
    setAvailableDates(dates);
    setSelectedDate(dates[0]); // Seleccionar hoy por defecto
  }, []);

  useEffect(() => {
    if (!slug || slug.trim() === '') {
      window.location.href = '/app/demo';
      return;
    }

    async function fetchBusinessData() {
      try {
        setLoading(true);
        let bData = null;
        let bError = null;
        try { bData = await fetchPublicBusiness(supabase, slug); } catch (e) { bError = e; }

        if (bError || !bData) throw new Error('Negocio no encontrado');
        
        setBusiness(bData);
        setServices(bData.services || []);
        
        const availableStaff = (bData.staff || []).filter(s => s.available);
        setStaff([
          { id: 'any', name: 'Cualquier profesional', is_any: true, role: 'Asignación automática' },
          ...availableStaff
        ]);

        setThemeColor(bData.minisite_theme_color || '#10b981');
        setShowStaffTab(bData.minisite_show_staff ?? true);

      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    
    if (slug) fetchBusinessData();
  }, [slug]);

  const categories = ['Todos los servicios', ...new Set(services.map(s => s.category || 'General'))];
  const filteredServices = services.filter(s => 
    (selectedCategory === 'Todos los servicios' || s.category === selectedCategory) &&
    s.nombre.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const cartTotal = cart.reduce((sum, item) => sum + (Number(item.precio) || 0), 0);
  const cartDuration = cart.reduce((sum, item) => sum + (Number(item.duracion) || 0), 0);
  const hasQuoteServices = cart.some(isQuoteService);
  
  const handleAddToCart = (service) => {
    if (!cart.find(item => item.id === service.id)) {
      setCart([...cart, service]);
    }
  };

  const handleRemoveFromCart = (serviceId) => {
    setCart(cart.filter(item => item.id !== serviceId));
  };

  const handleQuickBooking = () => {
    if (services.length === 0) {
      toast.error("No hay servicios disponibles en este momento");
      return;
    }
    if (cart.length === 0) {
      setCart([services[0]]);
    }
    if (!showStaffTab) {
      setSelectedStaff(staff.find(st => st.is_any) || staff[0]);
      setStep(3);
    } else {
      setStep(2);
    }
    toast.success("¡Iniciando tu Reserva Rápida!", { icon: '⚡' });
  };

  const generateTimeSlots = () => {
    const slots = [];
    if (timeFilter === 'mañana') {
      for (let i = 8; i <= 11; i++) { slots.push(`${i}:00`); slots.push(`${i}:30`); }
    } else if (timeFilter === 'tarde') {
      for (let i = 12; i <= 17; i++) { slots.push(`${i}:00`); slots.push(`${i}:30`); }
    } else {
      for (let i = 18; i <= 21; i++) { slots.push(`${i}:00`); slots.push(`${i}:30`); }
    }
    return slots;
  };

  const formatShortDate = (date) => {
    if (!date) return '';
    const days = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];
    return {
      dayName: days[date.getDay()],
      dayNumber: date.getDate(),
      full: date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })
    };
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h} h ${m} min` : `${h} h`;
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return alert('Por favor, selecciona al menos un servicio.');
    setIsSubmitting(true);
    try {
      const keylaCode = `KEY-${Math.floor(1000 + Math.random() * 9000)}`;
      
      // Concatenar nombres de servicios para compatibilidad con la DB actual
      const serviceNames = cart.map(s => s.nombre).join(', ');
      
      const bookingDate = localDateStr(selectedDate || new Date());
      const staffName = selectedStaff?.is_any ? null : (selectedStaff?.name || null);
      // La cita la crea la base (supabase/migrations/20261009c): valida el negocio, los servicios, el profesional, el horario y los topes.
      const newAppointment = await bookPublicAppointment(supabase, {
        slug, name: clientData.name, phone: clientData.phone, serviceIds: cart.map((s) => s.id), staff: staffName, date: bookingDate, time: selectedTime,
        legacyRow: {
          id: Date.now().toString(), business_id: business.id, client: clientData.name, phone: clientData.phone, service: serviceNames, date: bookingDate, time: selectedTime,
          status: 'confirmada', staffassigned: staffName || 'Asignación Automática', source: 'web_booking'
        }
      });
      setBookedCode(keylaCode);

      const webhookPayload = {
        event: 'new_web_booking',
        business_name: business.nombre_negocio,
        slug: slug,
        webhook_n8n: business.webhook_n8n,
        appointment: newAppointment,
        cart_total: cartTotal,
        cart_details: cart
      };
      
      // Si la reserva ya existía (doble clic, recarga) no se avisa dos veces al flujo.
      if (!newAppointment.duplicada) {
        const targetWebhook = business.webhook_n8n || "https://flow.seryiads.com/webhook/keira-router-central";
        await fetch(targetWebhook, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(webhookPayload)
        }).catch(e => console.error("Webhook notification failed:", e));
      }

      setStep(5);
    } catch (err) {
      console.error(err);
      alert(publicBookingError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyAppointment = async (e) => {
    e.preventDefault();
    setSearchError('');
    setVerifiedAppointment(null);
    try {
      const code = verificationSearchCode.trim().toUpperCase();
      if (!code) return;
      if (code === bookedCode) {
         setVerifiedAppointment({
           service: cart.map(s => s.nombre).join(', '),
           status: 'confirmada',
           date: selectedDate ? localDateStr(selectedDate) : '',
           time: selectedTime,
           staffassigned: selectedStaff?.name
         });
      } else {
         setSearchError("No encontramos una cita con ese código en esta sesión.");
      }
    } catch (err) {
      setSearchError("Error al buscar la cita.");
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
      <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (!business) return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] text-white">
      Sitio no encontrado
    </div>
  );

  // Negocios de pedidos y domicilios (agua, tienda, distribuidora): el minisitio es para pedir productos, no para reservar citas.
  if (business.flujo_operativo === 'pedidos') {
    return <PublicOrderPortal business={business} slug={slug} themeColor={themeColor} />;
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-white font-sans pb-24 selection:bg-white/20">
      
      {/* HEADER PREMIUM ESTILO WEIBOOK (FULL WIDTH) */}
      <header className="relative w-full h-[300px] md:h-[400px]">
        {/* Banner Background */}
        <div className="absolute inset-0 bg-[#1A1A1A]">
          {business.cover_image_url || business.banner_url ? (
            <img 
              src={business.cover_image_url || business.banner_url} 
              alt="Cover" 
              className="w-full h-full object-cover opacity-90"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A]"></div>
          )}
          {/* Gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/60 to-transparent"></div>
        </div>

        {/* Business Info Overlay - Bottom Left */}
        <div className="absolute bottom-0 left-0 w-full p-4 md:p-8 lg:px-12 flex flex-col justify-end z-10">
          <div className="max-w-7xl mx-auto w-full flex flex-col md:flex-row md:items-end gap-6">
            <div className="w-24 h-24 md:w-32 md:h-32 bg-[#1A1A1A] rounded-2xl p-1.5 shadow-2xl border border-white/10 flex-shrink-0 relative overflow-hidden">
              {business.logo_url ? (
                <img src={business.logo_url} alt="Logo" className="w-full h-full object-cover rounded-xl" />
              ) : (
                <div className="w-full h-full bg-slate-800 rounded-xl flex items-center justify-center text-white font-black text-4xl">
                  {business.nombre_negocio.charAt(0)}
                </div>
              )}
            </div>
            <div className="pb-2">
              <h1 className="font-black text-3xl md:text-5xl text-white tracking-tight">{business.nombre_negocio}</h1>
              <div className="flex flex-wrap items-center gap-3 md:gap-4 mt-3 text-sm font-semibold">
                <span className="bg-white/10 px-3 py-1.5 rounded-full backdrop-blur-md border border-white/5 uppercase tracking-wider text-xs">{business.tipo_negocio || 'Negocio'}</span>
                <span className="flex items-center gap-1.5 text-yellow-500 bg-white/5 px-3 py-1.5 rounded-full backdrop-blur-md border border-white/5">
                  ⭐ {business.rating || '4.9'} <span className="text-slate-300 font-normal ml-1">(100+ reseñas)</span>
                </span>
                <span className="flex items-center gap-1 text-slate-300">
                  <MapPin size={16}/> {business.direccion || 'Pereira, Colombia'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* TABS NAVEGACIÓN STICKY */}
      <div className="sticky top-0 z-40 bg-[#09090b]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 lg:px-12">
          <div className="flex items-center gap-8 overflow-x-auto no-scrollbar">
            <button
              onClick={() => setActivePortalTab('booking')}
              className={`relative py-4 text-sm font-bold transition-all duration-300 whitespace-nowrap ${
                activePortalTab === 'booking' ? 'text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              {activePortalTab === 'booking' && (
                <motion.div layoutId="navTab" className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: themeColor }} />
              )}
              Servicios
            </button>
            
            {showStaffTab && (
              <button
                onClick={() => setActivePortalTab('staff')}
                className={`relative py-4 text-sm font-bold transition-all duration-300 whitespace-nowrap ${
                  activePortalTab === 'staff' ? 'text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                {activePortalTab === 'staff' && (
                  <motion.div layoutId="navTab" className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: themeColor }} />
                )}
                Equipo
              </button>
            )}

            <button
              onClick={() => setActivePortalTab('verificar')}
              className={`relative py-4 text-sm font-bold transition-all duration-300 whitespace-nowrap ${
                activePortalTab === 'verificar' ? 'text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              {activePortalTab === 'verificar' && (
                <motion.div layoutId="navTab" className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: themeColor }} />
              )}
              Mi Cita
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-12 py-8 flex flex-col lg:flex-row gap-8">
        
        {/* COLUMNA IZQUIERDA (FLUJO PRINCIPAL) */}
        <div className="flex-1 space-y-12">
          
          {/* SECCIÓN SOBRE NOSOTROS (Siempre visible si no es checkout) */}
          {activePortalTab === 'booking' && step === 1 && (
            <section id="sobre-nosotros" className="space-y-4">
              <h2 className="font-bold text-2xl text-white">Sobre nosotros</h2>
              <div className="text-slate-300 leading-relaxed text-sm">
                {business.descripcion ? (
                  <p>{business.descripcion}</p>
                ) : (
                  <p>Bienvenido a {business.nombre_negocio}. Disfruta de la mejor experiencia y servicio garantizado.</p>
                )}
              </div>
            </section>
          )}

          {activePortalTab === 'booking' && step === 1 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
              
              {/* Bloque ¿Lo de siempre? */}
              <div className="bg-[#1A1A1A] border border-white/5 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center font-bold" style={{ color: themeColor }}>VIP</div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium">Hola, ¿lo de siempre?</p>
                    <p className="font-extrabold text-white text-base">RESERVA RÁPIDA (1-CLICK)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5 w-full sm:w-auto">
                   <button 
                     onClick={handleQuickBooking} 
                     className="flex-1 sm:flex-initial flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-lg active:scale-95"
                     style={{ backgroundColor: themeColor, color: '#09090b' }}
                   >
                     ⚡ Reservar Ya
                   </button>
                   <button 
                     onClick={() => setShowVipModal(true)} 
                     className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 px-3.5 py-2.5 rounded-xl text-xs font-bold text-white transition-colors"
                   >
                     <QrCode size={15} /> Pase VIP
                   </button>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Seleccionar servicios</h2>
                
                {/* Buscador */}
                <div className="relative mb-6">
                  <Search className="absolute left-4 top-3.5 text-slate-400" size={18} />
                  <input 
                    type="text" 
                    placeholder="Buscar servicios..." 
                    className="w-full bg-[#1A1A1A] border border-white/10 rounded-2xl pl-11 pr-4 py-3 text-sm text-white focus:outline-none transition-colors"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ outlineColor: themeColor }}
                  />
                </div>

                {/* Categorías */}
                <div className="flex overflow-x-auto gap-2 pb-4 no-scrollbar">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                        selectedCategory === cat ? 'text-white' : 'bg-white/5 text-slate-400 hover:text-white'
                      }`}
                      style={{ backgroundColor: selectedCategory === cat ? themeColor : '' }}
                    >
                      {cat} {cat === 'Todos los servicios' && <span className="ml-1 opacity-60">({services.length})</span>}
                    </button>
                  ))}
                </div>

                {/* Lista de Servicios */}
                <div className="space-y-3 mt-4">
                  {filteredServices.map(service => {
                    const isInCart = cart.some(item => item.id === service.id);
                    const isQuote = isQuoteService(service);

                    return (
                      <div key={service.id} className={`p-4 rounded-2xl border transition-all flex flex-col md:flex-row md:items-center gap-4 ${isInCart ? 'bg-white/10 border-[#3b82f6]' : 'bg-[#1A1A1A] border-white/5 hover:border-white/20'}`} style={isInCart ? { borderColor: themeColor } : {}}>
                         <div className="w-24 h-24 rounded-xl bg-[#222] flex-shrink-0 overflow-hidden">
                           {service.image_url ? (
                             <img src={service.image_url} alt={service.nombre} className="w-full h-full object-cover" />
                           ) : (
                             <div className="w-full h-full flex items-center justify-center text-slate-600"><Package size={32}/></div>
                           )}
                         </div>
                         <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <h3 className="font-bold text-white text-lg">{service.nombre}</h3>
                              {isQuote ? (
                                <span className="text-xs font-extrabold uppercase px-2.5 py-1 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30">
                                  A Cotizar
                                </span>
                              ) : (
                                <p className="font-bold text-white text-lg">${Number(service.precio).toLocaleString('es-CO')}</p>
                              )}
                            </div>
                            <p className="text-sm text-slate-400 mt-1 line-clamp-2">{service.descripcion || 'Sin descripción'}</p>
                            
                            <div className="flex items-center justify-between mt-4">
                              <div className="flex items-center gap-1.5 text-xs text-slate-400 font-semibold">
                                <Clock size={14} /> {formatDuration(service.duracion_minutos || service.duracion || 30)}
                              </div>
                              {isInCart ? (
                                <div className="flex items-center gap-3">
                                  <button onClick={() => handleRemoveFromCart(service.id)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-red-500/20 hover:text-red-400 transition-colors">
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              ) : (
                                <button 
                                  onClick={() => handleAddToCart(service)}
                                  className="text-white px-5 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-colors hover:opacity-80"
                                  style={{ backgroundColor: themeColor }}
                                >
                                  + Añadir
                                </button>
                              )}
                            </div>
                         </div>
                      </div>
                    );
                  })}
                </div>

              </div>
            </motion.div>
          )}

          {activePortalTab === 'booking' && step === 2 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="flex items-center gap-2 cursor-pointer text-slate-400 hover:text-white transition-colors w-fit mb-4" onClick={() => setStep(1)}>
                <ChevronLeft size={16}/> <span className="text-sm font-bold">Volver</span>
              </div>
              <div>
                <p className="font-bold text-xs tracking-widest uppercase mb-2" style={{ color: themeColor }}>Paso 2</p>
                <h2 className="text-3xl font-black text-white">Elige tu profesional</h2>
                <p className="text-slate-400 mt-1">Selecciona la persona con la que deseas agendar.</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {staff.map(member => (
                  <button 
                    key={member.id}
                    onClick={() => { setSelectedStaff(member); setStep(3); }}
                    className={`p-5 rounded-3xl border text-center transition-all flex flex-col items-center justify-center gap-3 ${
                      selectedStaff?.id === member.id ? 'bg-white/10' : 'bg-[#1A1A1A] border-white/5 hover:border-white/20'
                    }`}
                    style={selectedStaff?.id === member.id ? { borderColor: themeColor } : {}}
                  >
                    <div className="w-20 h-20 rounded-full bg-[#222] p-1 border-2 overflow-hidden relative" style={{ borderColor: themeColor }}>
                      {member.image_url ? (
                        <img src={member.image_url} alt={member.name} className="w-full h-full object-cover rounded-full" />
                      ) : (
                        <div className="w-full h-full bg-slate-800 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                          {member.is_any ? '*' : member.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm leading-tight">{member.name}</h3>
                      <div className="flex items-center justify-center gap-2 mt-2">
                        <span className="bg-white/5 px-2 py-0.5 rounded-md text-[10px] uppercase font-semibold text-slate-300 tracking-wider">{member.role || 'Staff'}</span>
                        {!member.is_any && (
                          <span className="flex items-center gap-1 text-yellow-500 text-[11px] font-bold">
                            <Star size={10} fill="currentColor" /> 5
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {activePortalTab === 'booking' && step === 3 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="flex items-center gap-2 cursor-pointer text-slate-400 hover:text-white transition-colors w-fit mb-4" onClick={() => setStep(showStaffTab ? 2 : 1)}>
                <ChevronLeft size={16}/> <span className="text-sm font-bold">Volver</span>
              </div>
               <div>
                <p className="font-bold text-xs tracking-widest uppercase mb-2" style={{ color: themeColor }}>Paso 3</p>
                <h2 className="text-3xl font-black text-white">Elige Fecha y Hora</h2>
              </div>
              
              <div className="bg-[#1A1A1A] border border-white/5 rounded-3xl p-6">
                <div className="flex items-center justify-between mb-6">
                   <h3 className="font-bold text-lg text-white">Horarios disponibles</h3>
                   <span className="text-sm text-slate-400 capitalize">{formatShortDate(selectedDate).full}</span>
                </div>

                {/* Horizontal Date Picker */}
                <div className="flex items-center gap-3 overflow-x-auto pb-4 no-scrollbar">
                  {availableDates.map((date, idx) => {
                    const isSelected = selectedDate && date.getDate() === selectedDate.getDate();
                    const fmt = formatShortDate(date);
                    return (
                      <button 
                        key={idx}
                        onClick={() => setSelectedDate(date)}
                        className={`flex-shrink-0 w-[72px] h-[84px] rounded-2xl border transition-all flex flex-col items-center justify-center gap-1 ${
                          isSelected ? 'text-white' : 'bg-[#111111] border-white/10 text-slate-400 hover:border-white/30'
                        }`}
                        style={isSelected ? { backgroundColor: themeColor, borderColor: themeColor } : {}}
                      >
                        <span className="text-[10px] font-bold uppercase tracking-wider">{fmt.dayName}</span>
                        <span className="text-2xl font-black">{fmt.dayNumber}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Day Part Toggles */}
                <div className="flex p-1 bg-[#111111] rounded-xl mt-6">
                   {['mañana', 'tarde', 'noche'].map(part => (
                     <button 
                       key={part}
                       onClick={() => setTimeFilter(part)}
                       className={`flex-1 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                         timeFilter === part ? 'text-white shadow-lg' : 'text-slate-400 hover:text-white'
                       }`}
                       style={timeFilter === part ? { backgroundColor: themeColor } : {}}
                     >
                       {part}
                     </button>
                   ))}
                </div>

                {/* Time Slots */}
                <div className="mt-6">
                  <p className="text-sm font-semibold text-slate-300 mb-4 capitalize">{generateTimeSlots().length} Horarios disponibles: {timeFilter}</p>
                  <div className="grid grid-cols-3 gap-3">
                    {generateTimeSlots().map(time => (
                      <button 
                        key={time}
                        onClick={() => { setSelectedTime(time); setStep(4); }}
                        className="py-4 rounded-xl border border-white/10 bg-[#111111] hover:bg-white/5 hover:border-white/30 transition-all font-bold text-white text-lg flex items-center justify-center"
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activePortalTab === 'booking' && step === 4 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="flex items-center gap-2 cursor-pointer text-slate-400 hover:text-white transition-colors w-fit mb-4" onClick={() => setStep(3)}>
                <ChevronLeft size={16}/> <span className="text-sm font-bold">Volver</span>
              </div>
              <div>
                <p className="font-bold text-xs tracking-widest uppercase mb-2" style={{ color: themeColor }}>Último Paso</p>
                <h2 className="text-3xl font-black text-white">Tus Datos Personales</h2>
              </div>

              <form id="booking-form" onSubmit={handleBookingSubmit} className="space-y-5">
                <div className="bg-[#1A1A1A] p-6 rounded-3xl border border-white/5 space-y-4">
                  <div>
                    <label className="text-xs uppercase font-bold text-slate-400 ml-1 mb-2 block">Nombre completo *</label>
                    <input 
                      type="text" required placeholder="Ej. Sergio Reina"
                      className="w-full bg-[#111111] border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none transition-all"
                      value={clientData.name} onChange={(e) => setClientData({...clientData, name: e.target.value})}
                      style={{ focusBorderColor: themeColor }}
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-bold text-slate-400 ml-1 mb-2 block">Correo electrónico *</label>
                    <input 
                      type="email" required placeholder="correo@ejemplo.com"
                      className="w-full bg-[#111111] border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none transition-all"
                      value={clientData.email} onChange={(e) => setClientData({...clientData, email: e.target.value})}
                    />
                    <p className="text-[11px] text-slate-500 mt-2 ml-1">Si lo dejas, te llega confirmación por mail.</p>
                  </div>
                  <div>
                    <label className="text-xs uppercase font-bold text-slate-400 ml-1 mb-2 block">Teléfono celular *</label>
                    <input 
                      type="tel" required placeholder="300 000 0000"
                      className="w-full bg-[#111111] border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:outline-none transition-all"
                      value={clientData.phone} onChange={(e) => setClientData({...clientData, phone: e.target.value})}
                    />
                  </div>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex gap-4">
                   <div className="mt-0.5" style={{ color: themeColor }}><ShieldCheck size={20} /></div>
                   <div>
                     <h4 className="font-bold text-white text-sm mb-1">Política de cancelación</h4>
                     <p className="text-xs text-slate-400 leading-relaxed">
                       Si necesitas cancelar o reprogramar tu cita, te agradecemos avisarnos con al menos 3 horas de anticipación.
                       <br/><br/>
                       Las cancelaciones de última hora o inasistencias repetidas podrán requerir un anticipo para futuras reservas.
                     </p>
                   </div>
                </div>
              </form>
            </motion.div>
          )}

          {activePortalTab === 'booking' && step === 5 && (
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-[#1A1A1A] border border-white/5 rounded-3xl p-8 text-center max-w-lg mx-auto mt-12">
               <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                 <div className="absolute inset-0 rounded-full animate-ping opacity-20" style={{ backgroundColor: themeColor }}></div>
                 <div className="absolute inset-0 rounded-full opacity-20" style={{ backgroundColor: themeColor }}></div>
                 <CheckCircle size={48} style={{ color: themeColor }} className="relative z-10" />
               </div>
               <h2 className="text-3xl font-black text-white mb-2">¡Reserva Confirmada!</h2>
               <p className="text-slate-400 mb-8">Hemos enviado los detalles a tu WhatsApp y correo electrónico.</p>
               
               <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 mb-8 shadow-inner">
                 <p className="text-xs text-slate-500 mb-2 uppercase font-bold tracking-widest">Código de Cita</p>
                 <p className="font-black text-4xl tracking-widest font-mono" style={{ color: themeColor }}>{bookedCode}</p>
                 <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">Guarda este código para consultar tu progreso VIP.</p>
               </div>

               <button onClick={() => window.location.reload()} className="text-white w-full py-4 rounded-2xl font-bold transition-colors hover:opacity-80" style={{ backgroundColor: themeColor }}>
                 Hacer otra reserva
               </button>
            </motion.div>
          )}

          {/* TAB EQUIPO */}
          {activePortalTab === 'staff' && (
            <section className="space-y-6">
              <h2 className="font-bold text-2xl text-white">Nuestro Equipo</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {staff.map(st => (
                  <div key={st.id} className="bg-[#121214] border border-white/5 p-6 rounded-3xl flex items-center gap-6">
                    <div className="w-20 h-20 rounded-2xl bg-white/5 flex-shrink-0 relative overflow-hidden">
                      {st.image_url ? (
                        <img src={st.image_url} alt={st.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500 font-bold text-2xl">
                          {st.is_any ? '*' : st.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-white mb-1">{st.name}</h3>
                      <p className="text-sm text-slate-400">{st.role || (st.is_any ? 'Aleatorio' : 'Staff')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* TAB VERIFICAR CITA */}
          {activePortalTab === 'verificar' && (
            <section className="space-y-6">
              <h2 className="font-bold text-2xl text-white">Consultar Mi Cita</h2>
              
              <form onSubmit={handleVerifyAppointment} className="bg-[#121214] p-6 rounded-3xl border border-white/5">
                <label className="text-[10px] uppercase font-bold text-slate-400 ml-1 mb-1 block">Tu Código de Reserva</label>
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    placeholder="KEY-XXXX"
                    required
                    className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none uppercase font-mono"
                    value={verificationSearchCode} 
                    onChange={(e) => setVerificationSearchCode(e.target.value)}
                  />
                  <button 
                    type="submit" 
                    className="bg-white/10 hover:bg-white/20 text-white font-bold px-6 py-3.5 rounded-xl text-sm transition-colors"
                  >
                    Buscar
                  </button>
                </div>
                {searchError && <p className="text-red-400 text-xs mt-3 ml-1">{searchError}</p>}
              </form>

              {verifiedAppointment && (
                <div className="bg-[#121214] p-6 rounded-3xl border border-white/5 animate-in fade-in">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <p className="text-xs text-slate-400 uppercase font-bold tracking-widest mb-1">Estado de Cita</p>
                      <h3 className="font-black text-2xl text-white">{verifiedAppointment.service}</h3>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                      verifiedAppointment.status === 'confirmada' ? 'bg-emerald-500/20 text-emerald-400' :
                      verifiedAppointment.status === 'completada' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {verifiedAppointment.status.toUpperCase()}
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-slate-400"><Calendar size={14}/></div>
                      <span className="text-white font-semibold">{verifiedAppointment.date} a las {verifiedAppointment.time}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-slate-400"><Clock size={14}/></div>
                      <span className="text-white font-semibold">Con {verifiedAppointment.staffassigned || 'Equipo Seryi'}</span>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

        </div>

        {/* COLUMNA DERECHA (RESUMEN CARRITO) */}
        {activePortalTab === 'booking' && step < 5 && (
          <div className="w-full lg:w-[380px] flex-shrink-0">
             <div className="sticky top-24 bg-[#1A1A1A] border border-white/5 rounded-3xl p-6 shadow-2xl">
               <div className="flex items-center justify-between mb-6">
                 <h3 className="font-black text-xl text-white">Resumen</h3>
                 <div className="w-10 h-10 rounded-full text-white flex items-center justify-center relative" style={{ backgroundColor: themeColor }}>
                   <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-[#1A1A1A]">{cart.length}</div>
                   <Package size={18} />
                 </div>
               </div>
               
               {selectedDate && selectedTime && (
                 <p className="text-sm font-semibold capitalize mb-4" style={{ color: themeColor }}>
                   {formatShortDate(selectedDate).full} • {selectedTime}
                 </p>
               )}

               {cart.length === 0 ? (
                 <div className="py-12 text-center border-2 border-dashed border-white/10 rounded-2xl">
                   <p className="text-slate-500 text-sm font-semibold">Tu carrito está vacío</p>
                 </div>
               ) : (
                 <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 no-scrollbar">
                   {cart.map((item, idx) => (
                     <div key={idx} className="flex gap-4 p-3 rounded-2xl bg-[#111111] border border-white/5 relative group">
                       <div className="w-16 h-16 rounded-xl bg-[#222] overflow-hidden flex-shrink-0">
                         {item.image_url ? (
                           <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                         ) : (
                           <div className="w-full h-full flex items-center justify-center text-slate-600"><Package size={16}/></div>
                         )}
                       </div>
                       <div className="flex-1 py-1">
                         <h4 className="font-bold text-white text-sm leading-tight pr-6">{item.nombre}</h4>
                         {selectedStaff && <p className="text-[11px] text-slate-400 mt-1 line-clamp-1">Con: {selectedStaff.name}</p>}
                         <div className="flex items-center justify-between mt-2">
                           <span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={12}/> {formatDuration(item.duracion_minutos || item.duracion || 30)}</span>
                           {isQuoteService(item) ? (
                              <span className="text-xs font-extrabold text-amber-400">A cotizar</span>
                            ) : (
                              <span className="font-bold text-white text-sm">${Number(item.precio).toLocaleString('es-CO')}</span>
                            )}
                         </div>
                       </div>
                       <button onClick={() => handleRemoveFromCart(item.id)} className="absolute top-3 right-3 text-slate-600 hover:text-red-400 transition-colors">
                         <Trash2 size={16} />
                       </button>
                     </div>
                   ))}
                 </div>
               )}

               <div className="border-t border-white/10 mt-6 pt-6 space-y-3">
                 <div className="flex justify-between items-center text-sm">
                   <span className="text-slate-400 font-semibold">Total de servicios</span>
                   <span className="text-white font-bold">${cartTotal.toLocaleString('es-CO')}</span>
                 </div>
                 <div className="flex justify-between items-center text-lg mt-2">
                   <span className="text-white font-bold">Total a pagar</span>
                   <div className="text-right">
                      <span className="font-black" style={{ color: themeColor }}>${cartTotal.toLocaleString('es-CO')}</span>
                      {hasQuoteServices && (
                        <p className="text-[10px] text-amber-400 font-semibold mt-0.5">+ Servicios a cotizar</p>
                      )}
                    </div>
                 </div>
               </div>

               {step < 4 ? (
                 <button 
                   onClick={() => {
                     if (cart.length === 0) return alert('Selecciona un servicio primero');
                     if (step === 1 && !showStaffTab) {
                       setSelectedStaff(staff.find(st => st.is_any) || staff[0]);
                       setStep(3); // Skip staff selection
                     } else {
                       setStep(step + 1);
                     }
                   }}
                   className={`w-full py-4 rounded-2xl font-black mt-6 transition-all hover:opacity-80 ${
                     cart.length > 0 ? 'text-white shadow-lg' : 'bg-white/5 text-slate-500 cursor-not-allowed'
                   }`}
                   style={cart.length > 0 ? { backgroundColor: themeColor } : {}}
                 >
                   Continuar
                 </button>
               ) : (
                 <button 
                   type="submit"
                   form="booking-form"
                   disabled={isSubmitting}
                   className="w-full py-4 rounded-2xl font-black mt-6 text-white shadow-lg transition-all flex items-center justify-center gap-2 hover:opacity-80"
                   style={{ backgroundColor: themeColor }}
                 >
                   {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : 'Confirmar'}
                 </button>
               )}

               <div className="mt-4 flex items-center justify-center gap-2 text-[10px] uppercase font-bold text-slate-500">
                 <ShieldCheck size={14} className="text-slate-400" />
                 Pago seguro - Cifrado SSL
               </div>
             </div>
          </div>
        )}

      </div>

      {/* MODAL PASE VIP */}
      {showVipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/85 backdrop-blur-md">
          <div className="bg-[#FAF9F6] text-[#1A1A1A] w-full max-w-md rounded-[2.5rem] p-7 md:p-8 relative overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <button 
              onClick={() => setShowVipModal(false)} 
              className="absolute top-6 right-6 w-9 h-9 rounded-full bg-black/5 flex items-center justify-center hover:bg-black/10 transition-colors text-slate-700 font-bold"
            >
              ✕
            </button>
            
            <p className="font-extrabold text-xs tracking-widest uppercase mb-1.5" style={{ color: themeColor }}>Pase VIP Digital Seryiflow</p>
            <h3 className="font-black text-2xl md:text-3xl leading-tight mb-2">Lleva tu pase en el celular</h3>
            <p className="text-slate-600 text-xs md:text-sm mb-6 leading-relaxed">
              Escanea el código para consultar tus visitas acumuladas, próxima cita y beneficios exclusivos.
            </p>
            
            {/* QR Dinámico Real */}
            <div className="bg-white p-5 rounded-3xl shadow-xl mx-auto w-fit mb-5 border border-slate-200/80 flex flex-col items-center justify-center">
              <QRCodeSVG 
                value={typeof window !== 'undefined' ? `${window.location.origin}/app/${slug || 'demo'}?pass=vip&client=${encodeURIComponent(clientData.name || 'VIP')}` : `https://flow.seryiads.com/app/${slug || 'demo'}?pass=vip`}
                size={185}
                bgColor="#FFFFFF"
                fgColor="#0F172A"
                level="Q"
                includeMargin={true}
              />
              <span className="text-[10px] font-mono font-bold text-slate-400 mt-1 uppercase tracking-wider">
                TOKEN: {slug?.toUpperCase() || 'SERYI'}-VIP-PASS
              </span>
            </div>
            
            <p className="text-center text-xs font-bold text-slate-600 mb-4">
              📱 Abre la cámara de tu celular y apunta al QR
            </p>
            
            <div className="space-y-2.5">
              <button 
                onClick={() => {
                  const passUrl = typeof window !== 'undefined' ? `${window.location.origin}/app/${slug || 'demo'}?pass=vip` : `https://flow.seryiads.com/app/${slug || 'demo'}?pass=vip`;
                  navigator.clipboard.writeText(passUrl);
                  toast.success("¡Enlace del Pase VIP copiado!", { icon: '📋' });
                }} 
                className="w-full text-white font-extrabold py-3.5 rounded-2xl flex items-center justify-center gap-2 hover:brightness-110 transition-all shadow-lg text-xs md:text-sm uppercase tracking-wider" 
                style={{ backgroundColor: themeColor }}
              >
                📋 Copiar enlace del Pase VIP
              </button>

              {/* Botones de Integración Wallet */}
              <div className="grid grid-cols-2 gap-2.5 pt-1">
                <button 
                  onClick={() => {
                    toast.success("Conectando con Google Wallet Loyalty API...", { icon: '🎟️' });
                    const passUrl = `${window.location.origin}/app/${slug || 'demo'}?pass=vip`;
                    const walletPayload = {
                      event: 'generate_wallet_pass',
                      type: 'google_wallet',
                      business_name: business?.nombre_negocio || 'Negocio VIP',
                      business_slug: slug,
                      client_name: clientData.name || 'Cliente VIP',
                      url: passUrl
                    };
                    fetch(business?.webhook_n8n || "https://flow.seryiads.com/webhook/keylacrm-sync", {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(walletPayload)
                    }).catch(console.error);
                  }}
                  className="py-2.5 px-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all border border-slate-700 shadow-sm"
                >
                  <span>🎟️ Google Wallet</span>
                </button>

                <button 
                  onClick={() => {
                    toast.success("Conectando con Apple Wallet PassKit...", { icon: '🍏' });
                    const passUrl = `${window.location.origin}/app/${slug || 'demo'}?pass=vip`;
                    const walletPayload = {
                      event: 'generate_wallet_pass',
                      type: 'apple_wallet',
                      business_name: business?.nombre_negocio || 'Negocio VIP',
                      business_slug: slug,
                      client_name: clientData.name || 'Cliente VIP',
                      url: passUrl
                    };
                    fetch(business?.webhook_n8n || "https://flow.seryiads.com/webhook/keylacrm-sync", {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(walletPayload)
                    }).catch(console.error);
                  }}
                  className="py-2.5 px-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all border border-slate-700 shadow-sm"
                >
                  <span>🍏 Apple Wallet</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-8 mt-12 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500 font-semibold">
          <p>Desarrollado con ♥ por <a href="https://seryiads.com" target="_blank" rel="noreferrer" className="text-white hover:underline">Seryiads</a></p>
          <p>© {new Date().getFullYear()} Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
